import { useHandler } from "@redotech/react-util/hook";
import { StyleShadowRoot } from "@redotech/react-util/style";
import {
  HsaAutocheck,
  HsaCartWidgetPlacement,
} from "@redotech/redo-conversion-core-common/conversion/hsa-autocheck";
import { ModalRootContext } from "@redotech/redo-web/modal-root";
import { memo } from "react";
import { StorefrontClient } from "../../client/storefront";
import {
  isHsaIntentSignalEnabled,
  recordHsaIntentSignal,
} from "../../hsa/intent-signals";
import STYLE_MANAGER from "../../style-manager";
import { HsaCartWidget } from "./hsa-cart-widget";
import { useHsaCartEligibility } from "./use-hsa-cart-eligibility";
import { useHsaCartPortals } from "./use-hsa-cart-portals";

export const HsaCartInitializer = memo(function HsaCartInitializer({
  hsa,
  placements,
  storefrontClient,
}: {
  hsa: HsaAutocheck;
  placements: HsaCartWidgetPlacement[];
  storefrontClient: StorefrontClient;
}) {
  const summary = useHsaCartEligibility({ storefrontClient });
  const portals = useHsaCartPortals({ placements });
  // Same signal as the PDP badge, attributed to the cart widget.
  const onLearnMore = useHandler(() => {
    if (!isHsaIntentSignalEnabled(hsa, "cart_widget")) {
      return;
    }
    void recordHsaIntentSignal(storefrontClient, {
      source: "cart_widget",
      exclusive: false,
    });
  });

  // Hidden until eligibility is known; nothing renders for a cart with no
  // eligible items.
  if (
    portals.length === 0 ||
    summary === undefined ||
    summary.eligibleLineCount === 0
  ) {
    return null;
  }

  return (
    <>
      {portals.map(({ portal }, index) => (
        <StyleShadowRoot
          key={index}
          shadowRoot={portal.root}
          styleManager={STYLE_MANAGER}
        >
          <ModalRootContext.Provider value={portal.modalContainer}>
            <HsaCartWidget onLearnMore={onLearnMore} summary={summary} />
          </ModalRootContext.Provider>
        </StyleShadowRoot>
      ))}
    </>
  );
});
